/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This Page corresponds to the Admin Home
 * It will show a list of cabinet by default and can
 * display the AdminCabinetForm subpage when asked
 */

// Imports
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import * as lodash from 'lodash';
import { toast } from 'react-toastify';
import Switch from "react-switch";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-regular-svg-icons';
import { faArrowRightFromBracket, faMagnifyingGlass, faSortUp, faSortDown, faPenToSquare, faTrashCan, faPlusCircle, faSignal } from '@fortawesome/free-solid-svg-icons';

// Interfaces
import { ListedCabinet } from '../../interfaces/ICabinets';
import { AlertType } from '../../interfaces/IUtils';

// Utils
import { ErrorForUsr, displayAlert } from '../utils/Errors';
import { GlobalApiCall, HTTPResponse } from '../utils/ApiCallConstructor';
import { escapeRegExp } from '../utils/Format';

// subpages
import { AdminCabinetForm } from '../subpages/AdminCabinetForm';

// Buroclic adaptive components
import { PopUp } from '../buroclic/popUp';

// CSS
import 'bootstrap/dist/css/bootstrap.min.css';

enum SortableFields {
  Alias = 'cabinetAlias',
  Name = 'cabinetName',
  Status = 'isActive'
}

enum Order {
  asc = 'asc',
  desc = 'desc'
}


export const AdminHomePage = () => {
  document.title = 'Espace client | Admin';
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null); // HTML Element of the user menu

  const [currentCabinet, setCurrentCabinet] = useState<ListedCabinet>({} as ListedCabinet);
  const [cabinets, setCabinets] = useState<ListedCabinet[]>([]);

  // Header states
  const [searchedCabinet, setSearchedCabinet] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Table sorting
  const [sortField, setSortField] = useState(SortableFields.Alias);
  const [sortOrder, setSortOrder] = useState(Order.asc);
  const [matchingCabinets, setMatchingCabinets] = useState([] as Array<ListedCabinet>); // documents displayed from pagination

  // actions
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [toRefresh, setToRefresh] = useState(false);
  const [hasSignalsBeenChecked, setHasSignalsBeenChecked] = useState(false);
  const [healthStatus, setHealthStatus] = useState([]);


  // initial data
  /* eslint-disable */
  useEffect(() => {
    fetchCabinets(); // get it from BACK

    // Handle exiting menu with clicking everywhere else
    const handleExitingMenu = (event) => {
      // Hide the user menu
      if (menuRef.current && !menuRef.current.contains(event.target as Node))
        setIsMenuOpen(false);
    }
    document.addEventListener('click', handleExitingMenu); // Add listener
    return () => { document.removeEventListener('click', handleExitingMenu) } // remove listener

  }, []);


  // Look for cabinets which match the keywords
  useEffect(() => {

    let tmpMatchedCabinets = [] as Array<ListedCabinet>;
    if (searchedCabinet !== '') {
      // Only cabinet which name matches the user query
      const search = new RegExp(escapeRegExp(searchedCabinet), 'mi'); // user query
      tmpMatchedCabinets = cabinets.filter((el) => { return (search.test(el.cabinetAlias) || search.test(el.cabinetName)) });
    } else
      tmpMatchedCabinets = cabinets; // all cabinets

    // Filter cabinets
    if (sortField === SortableFields.Status) // isActive field
      setMatchingCabinets(lodash.orderBy(tmpMatchedCabinets, [(obj) => Number(obj.isActive)], [sortOrder]));
    else // Text field
      setMatchingCabinets(lodash.orderBy(tmpMatchedCabinets, [(obj) => obj[sortField]?.toLowerCase()], [sortOrder])); // filter by text (case insensitive)

  }, [cabinets, searchedCabinet, sortField, sortOrder]);


  useEffect(() => {
    if (toRefresh) {
      fetchCabinets();
      setToRefresh(false);
    }
  }, [toRefresh]);
  /* eslint-enable */


  /**
   * Retrieve and add a 'signal' property onto each index of the provided list of cabinet
   * in order to catch if the signal with the defined server is considered as OK or not
   * @param {ListedCabinet[]} cabinetsToProcess 
   * @param {Id} toastId 
   */
  const retrieveAllServSignals = async () => {
    try {
      const healthStatusResponse: HTTPResponse = await toast.promise(
        GlobalApiCall({
          endpoint: '/admin/cabinets/health',
          method: 'GET',
        }),
        {
          pending: 'Récupération des signaux des différents serveurs en cours. Cette opération devrait prendre au maximum 20 secondes.',
          error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
          success: `Les signaux ont été récupérés et analysés avec succès !`
        });

      if (healthStatusResponse.error) throw healthStatusResponse.error;
      else {
        setHasSignalsBeenChecked(true);
        setHealthStatus(healthStatusResponse.result as []);
      }

    } catch (err: any) {
      // Notify the user there's something weird
      toast.error(`Erreur : ${(err as HTTPResponse).error}`);

      if (err.error === ErrorForUsr.NotSignedIn) {
        localStorage.clear();
        navigate('/admin/login');
      }
      console.error(err.error)
    }
  }

// Load all cabinet records
const fetchCabinets = async () => {
  try {
    const cabinetsResponse: HTTPResponse = await toast.promise(
      GlobalApiCall({
        endpoint: '/admin/cabinets',
        method: 'GET',
      }),
      {
        pending: 'Récupération de la liste des cabinets en cours',
        error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
        success: `Liste des cabinets récupérée avec succès !`
      });

    // Make kind of a 'ping' on each cabinet's server to catch its signal (ON or OFF)
    if (cabinetsResponse.result)
      setCabinets(cabinetsResponse.result as Array<ListedCabinet>);
    else
      setCabinets([] as Array<ListedCabinet>);
  } catch (err: any) {
    // Notify the user there's something weird
    toast.error(`Erreur : ${(err as HTTPResponse).error}`);

    if (err.error === ErrorForUsr.NotSignedIn) {
      localStorage.clear();
      navigate('/admin/login');
    }
    console.error(err.error)
  }
}


const handleLogout = async () => {
  try {
    await toast.promise(
      GlobalApiCall({
        endpoint: '/account/signout',
        method: 'POST'
      }),
      {
        pending: 'Déconnexion en cours',
        error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
        success: `A bientôt !`
      });

    // redirect to login page
    localStorage.clear();
    navigate(`/admin/login`);
  } catch (err: any) { console.error(err.error); navigate(`/admin/login`); }
}


// change the sort order of a specific field
const sortCabinets = (field: SortableFields) => {
  // Since it's the same field, just change the order
  if (field === sortField)
    setSortOrder((sortOrder === Order.asc) ? Order.desc : Order.asc);
  else {
    // otherwise change the sorted field and set it to asc
    setSortField(field);
    setSortOrder(Order.asc);
  }
}


// Delete the current Cabinet (selected)
const deleteCabinet = async () => {
  if (currentCabinet.id <= 0) {
    displayAlert({ message: `Erreur : Aucun identifiant pour ce cabinet n'a pu être trouvé`, type: AlertType.error });
    return;
  }

  try {
    await toast.promise(
      GlobalApiCall({
        endpoint: `/admin/cabinets/${currentCabinet.id}`,
        method: 'DELETE',
      }),
      {
        pending: 'Suppression en cours ...',
        error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
        success: 'Cabinet supprimé'
      });

    fetchCabinets();
  } catch (err: any) {
    // Handle redirection
    if (err.error === ErrorForUsr.NotSignedIn) {
      localStorage.clear();
      navigate('/admin/login');
    }
    console.error(err.error)
  }

  // Hide the popup
  setIsDeletePopupOpen(false);
  setCurrentCabinet({} as ListedCabinet);
}



// UI functions

// Get the sorted icon of a column
const getSortIcon = (column: SortableFields) => {
  if (column === sortField)
    return (<FontAwesomeIcon className="float-right mr-2" icon={sortOrder === Order.asc ? faSortUp : faSortDown} />);
  else
    return (<></>);
}


return (
  <div className='flex flex-col flex-1 h-full'>

    {/* Header */}
    <header className="bg-gray-100 shadow-lg flex flex-row justify-center items-center w-full h-[10%]">

      {/* Logo */}
      <div className="h-auto w-1/6">
        <img src={`/images/buroclic.png`} alt="BuroClic" className="w-4/5 h-auto m-auto" />
      </div>

      {/* Search input */}
      <div className="w-4/6 flex flex-row justify-center h-12" tabIndex={1}>

        {/*className={`${inHeader ? 'px2 pl-4 py-1 text-2xl' : 'px-20 py-4 text-4xl'} leading-4 focus-visible:outline-none border border-gray-300 text-gray-500 w-full`}
                    style={{borderRadius: ` ${((searchedFolder !== dossier.DOS_TITRE) && searchResults.length > 0 && displayList) ? (inHeader ? '1.25rem 1.25rem 0px 0px' : '3rem 3rem 0px 0px') : (inHeader ? '2rem' : '3rem')}`}}*/}
        <input type="text" placeholder="Rechercher un cabinet" value={searchedCabinet} onChange={(e) => { setSearchedCabinet(e.target.value); }} tabIndex={2}
          className='px-10 py-2 text-2xl leading-4 border border-gray-300 focus-visible:outline-none rounded-lg text-gray-500 w-4/6' style={{ borderRadius: '3rem' }} />
        <FontAwesomeIcon className={`relative cursor-pointer h-6 w-6`} style={{ right: '65.5%', top: '0.7rem' }} icon={faMagnifyingGlass} />
      </div>

      {/* Menu */}
      <div className="w-1/6 flex flex-col items-center" ref={menuRef}>
        <FontAwesomeIcon className='h-8 w-8 text-[#E7812B]' icon={faCircleUser} onClick={() => setIsMenuOpen(!isMenuOpen)} tabIndex={3} />

        {/*<img
            src={'/images/profile.png'}
            alt="Profile"
            className="h-12 rounded-full cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            tabIndex={3}
          />*/}
        <p className="text-center mb-0 hidden lg:block">Administrateur</p>

        <div className={`absolute bg-gray-100 w-48 rounded-lg shadow-md right-4 ${isMenuOpen ? '' : 'hidden'}`} style={{ top: '10vh', zIndex: 9999 }}>

          { /* Check servers signals */}
          <div className="flex items-center hover:bg-zinc-50 rounded-lg cursor-pointer" onClick={() => { retrieveAllServSignals(); setIsMenuOpen(false); }}>
            <FontAwesomeIcon className="float-right ml-2 w-5 h-5 text-[#E7812B]" icon={faSignal} />
            <button className="block text-left px-2 py-2 text-gray-700">Vérifier les signaux</button>
          </div>

          { /* Déconnexion */}
          <div className="flex items-center hover:bg-zinc-50 rounded-lg cursor-pointer" onClick={handleLogout}>
            <FontAwesomeIcon className="float-right ml-2 w-5 h-5 text-[#E7812B]" icon={faArrowRightFromBracket} />
            <button className="block text-left px-2 py-2 text-gray-700">Déconnexion</button>
          </div>
        </div>
      </div>
    </header>


    {/* Delete confirmation */}
    <PopUp key={`delete-${String(isDeletePopupOpen)}`} title="Supprimer un espace client" className="popup-center"
      visible={isDeletePopupOpen} modal
      closeHandler={() => setIsDeletePopupOpen(false)}
      confirmLabel="Supprimer" confirmHandler={deleteCabinet}>
      <form onSubmit={deleteCabinet}>
        <span className='text-lg font-semibold'>Voulez-vous vraiment supprimer l'espace client de <span className='font-bold'>{(currentCabinet as ListedCabinet).cabinetName}</span> ?</span><br />
        <span className='italic font-semibold'>Pour rappel, le lien de connexion associé est :
          <a target='__blank' className='text-blue-500' href={`${window.origin}/${(currentCabinet as ListedCabinet).cabinetAlias}/login`}> {`${window.origin}/${(currentCabinet as ListedCabinet).cabinetAlias}/login`}</a>
        </span>
        <button type="submit" className="hidden"></button>
      </form>
    </PopUp>


    {/* Edit Button + form */}
    <FontAwesomeIcon className='absolute bottom-[5%] right-[5%] h-12 w-12 text-green-500 cursor-pointer' icon={faPlusCircle} onClick={() => { setIsEditPopupOpen(true) }} />
    <AdminCabinetForm key={String(isEditPopupOpen) + '-' + currentCabinet.cabinetName} cabinetId={currentCabinet.id}
      visible={isEditPopupOpen} setVisible={setIsEditPopupOpen} setCabinet={setCurrentCabinet} setToRefresh={setToRefresh} />


    {/* Rest of the page */}
    <div className='flex flex-col bg-white  w-full h-[90%]'>

      {/* Title */}
      <h2 className='text-center text-2xl font-bold my-4'>
        Pannel d'administration
      </h2>

      {/* Listing of cabinets */}
      <div className='w-full flex flex-col items-center overflow-auto'>
        <table className="w-4/5 border-1 z-1 border-top-0" key={`cablist-${cabinets.length}`}>
          <thead className='sticky top-0 z-50'>
            <tr className="bg-gray-200 h-16 text-lg">
              <th className={`p-2 w-20 text-left ${!hasSignalsBeenChecked ? 'hidden' : ''} `}>Signal</th>
              <th className="p-2 grow text-left cursor-pointer" onClick={() => sortCabinets(SortableFields.Alias)}>Alias{getSortIcon(SortableFields.Alias)}</th>
              <th className="p-2 grow text-left cursor-pointer" onClick={() => sortCabinets(SortableFields.Name)}>Nom{getSortIcon(SortableFields.Name)}</th>
              <th className="p-2 w-[15%] text-left">Nom de domaine</th>
              <th className="p-2 w-20 text-left">Port</th>
              <th className="p-2 w-1/5 text-left">Base de données</th>
              <th className="p-2 w-20 text-center cursor-pointer" onClick={() => sortCabinets(SortableFields.Status)}>Actif{getSortIcon(SortableFields.Status)}</th>
              <th className="p-2 w-20 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            { /* ONE ROW BY CABINET */
              matchingCabinets.map((cabinet, index) => (
                <tr key={cabinet.id} className={`cursor-default h-16 ${index % 2 === 0 ? 'bg-gray-100 border-b border-gray-300' : 'bg-white'}`}>
                  <td key={cabinet.cabinetAlias + '-' + healthStatus[cabinet.cabinetAlias]} className={`p-2 grow text-left ${!hasSignalsBeenChecked ? 'hidden' : ''} `}><span className={`w-3 h-3 block m-auto rounded-full ${!cabinet.isActive ? 'hidden' : ''} ${healthStatus[cabinet.cabinetAlias] ? 'bg-green-500' : 'bg-red-500'}`}></span></td>
                  <td className='p-2 grow text-left'>{cabinet.cabinetAlias}</td>
                  <td className='p-2 grow text-left'>{cabinet.cabinetName}</td>
                  <td className='p-2 w-[15%] text-left'>{cabinet.host}</td>
                  <td className='p-2 w-20 text-left'>{cabinet.port}</td>
                  <td className='p-2 w-1/5 text-left'>{cabinet.dbName}</td>
                  <td className='p-2 w-20 text-center'>
                    <Switch className='w-full h-full' disabled onChange={() => { }} checked={!!cabinet.isActive} />
                  </td>
                  <td className='className="p-2 w-20 text-center'>
                    <button onClick={() => { setCurrentCabinet(cabinet); setIsEditPopupOpen(true) }}><FontAwesomeIcon className="w-5 h-5 mx-2 text-blue-400" icon={faPenToSquare} /></button>
                    <button onClick={() => { setCurrentCabinet(cabinet); setIsDeletePopupOpen(true) }}><FontAwesomeIcon className="w-5 h-5 text-red-400" icon={faTrashCan} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Totalisation */}
      <div className="w-full flex flex-col justify-start items-center h-12 mt-2 min-h-[40px] text-right">
        <div className={`w-4/5 mr-8 flex ${hasSignalsBeenChecked ? 'justify-between' : 'justify-end'} items-center`}>

          {/* Signal legend */}
          {hasSignalsBeenChecked ?
            <span className="flex italic items-center">
              <span className="w-3 h-3 block my-auto mr-1 rounded-full bg-green-500"></span>Connexion établie
              <span className="w-3 h-3 block ml-4 my-auto mr-1 rounded-full bg-red-500"></span>Connexion non établie
            </span>
            : <></>}

          <div className='ml-4'>
            <span className="ml-4 font-semibold">{matchingCabinets.length} cabinet(s) affiché(s)</span>
            <span className={`${matchingCabinets.length === cabinets.length ? 'hidden' : 'ml-1'}`}>
              sur les <span className="font-semibold">{cabinets.length} cabinet(s)</span>
            </span>
          </div>
        </div>
      </div>
    </div>

  </div>
);
};